/**
 * Allys About Page Skeleton
 *
 * Matches AboutContentAllys: page hero, company story, values grid,
 * team section, locations grid, stats, and CTA.
 * Includes navigation skeleton so the nav bar is always visible.
 */

import { AllysNavSkeleton } from './AllysNavSkeleton';

export function AllysAboutSkeleton() {
  return (
    <main className="min-h-screen">
      <AllysNavSkeleton />

      {/* Page hero */}
      <section className="relative py-28 lg:py-36 overflow-hidden bg-gray-200 skeleton-stagger-1">
        <div className="absolute inset-0 skeleton-shimmer" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="h-4 w-20 rounded-full bg-white/20 mx-auto mb-4 skeleton-shimmer" />
          <div className="h-12 w-80 max-w-full rounded-lg bg-white/20 mx-auto mb-4 skeleton-shimmer" />
          <div className="h-5 w-96 max-w-full rounded bg-white/10 mx-auto skeleton-shimmer" />
        </div>
      </section>

      {/* Company story */}
      <section className="py-20 lg:py-28 bg-white skeleton-stagger-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="space-y-4">
              <div className="h-4 w-24 rounded-full bg-gray-200 skeleton-shimmer" />
              <div className="h-8 w-64 rounded-lg bg-gray-200 skeleton-shimmer" />
              <div className="space-y-2 pt-4">
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-5/6 rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-3/4 rounded bg-gray-100 skeleton-shimmer" />
              </div>
            </div>
            <div className="aspect-4/3 rounded-2xl bg-gray-100 skeleton-shimmer" />
          </div>
        </div>
      </section>

      {/* Values — 3 columns */}
      <section className="py-20 lg:py-28 bg-gray-50 skeleton-stagger-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 space-y-3">
            <div className="h-4 w-20 rounded-full bg-gray-200 mx-auto skeleton-shimmer" />
            <div className="h-8 w-48 rounded-lg bg-gray-200 mx-auto skeleton-shimmer" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-8 rounded-2xl bg-white border border-gray-100">
                <div className="h-12 w-12 rounded-xl bg-gray-200 mb-4 skeleton-shimmer" />
                <div className="h-6 w-32 rounded bg-gray-200 mb-3 skeleton-shimmer" />
                <div className="space-y-2">
                  <div className="h-3 w-full rounded bg-gray-100 skeleton-shimmer" />
                  <div className="h-3 w-5/6 rounded bg-gray-100 skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team section */}
      <section className="py-20 lg:py-28 bg-white skeleton-stagger-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 space-y-3">
            <div className="h-4 w-16 rounded-full bg-gray-200 mx-auto skeleton-shimmer" />
            <div className="h-8 w-40 rounded-lg bg-gray-200 mx-auto skeleton-shimmer" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-start gap-6 p-6 rounded-2xl bg-gray-50">
                <div className="w-24 h-24 rounded-2xl bg-gray-200 shrink-0 skeleton-shimmer" />
                <div className="space-y-2 flex-1 pt-2">
                  <div className="h-5 w-32 rounded bg-gray-200 skeleton-shimmer" />
                  <div className="h-4 w-24 rounded bg-gray-100 skeleton-shimmer" />
                  <div className="h-3 w-full rounded bg-gray-100 mt-3 skeleton-shimmer" />
                  <div className="h-3 w-3/4 rounded bg-gray-100 skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#243462] skeleton-stagger-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="text-center space-y-2">
                <div className="h-10 w-20 rounded-lg bg-white/15 mx-auto skeleton-shimmer" />
                <div className="h-4 w-24 rounded bg-white/10 mx-auto skeleton-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#040813] skeleton-stagger-6">
        <div className="max-w-3xl mx-auto px-4 text-center space-y-6">
          <div className="h-10 w-72 rounded-lg bg-white/10 mx-auto skeleton-shimmer" />
          <div className="h-5 w-64 rounded bg-white/5 mx-auto skeleton-shimmer" />
          <div className="h-12 w-40 rounded-xl bg-[#BBA339]/20 mx-auto skeleton-shimmer" />
        </div>
      </section>
    </main>
  );
}
